import React, { useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { getSerialNumberAndVehicleArea } from 'utils/utils'
import { isArray } from 'utils/types'
import { isEmpty } from 'ramda'
import { useDispatch } from 'react-redux'
import { useSelector, useMakeSelector } from 'hooks/redux'
import { makeGetIsWaiting } from 'selectors/application'
import {
  fetchSwitchLinkedFeatures,
  fetchSwitchLinkedReferenceParameters,
  SWITCH_LINKED_FEATURES_RECIEVED,
  SWITCH_LINKED_REFERENECE_PARAMETERS_RECIEVED,
} from 'actions/dash'
import {
  ECP_LINKED_FEATURES_RECIEVED,
  fetchEcpLinkedFeatures,
} from 'actions/ecp'
import { getTruckType } from 'selectors/truck'
import { getSwitchLinkedLogic } from 'selectors/dash'
import { getEcpLinkedLogics } from 'selectors/ecp'
import { VEHICLE_TYPE_CHEC_PLUS, VEHICLE_TYPE_M2 } from 'utils/constants'
import strings from 'utils/strings'
import { Paragraph, Loading, Tooltip } from 'components/patterns'
import { Link, useLocation } from 'react-router-dom'
import {
  StyledParagraph,
  StyledParamList,
  StyledRefreshIcon,
  ListLogicItem,
  StyledEcpLabel,
  StyledEcpBodyText,
} from './LinkedLogicsList.style'

/* eslint-disable react-hooks/exhaustive-deps */
const LinkedLogicsList = ({
  className,
  switchId,
  isNativeSwitch,
  outputId,
  moduleType,
  associatedControl,
}) => {
  const dispatch = useDispatch()
  const { serialNumber } = getSerialNumberAndVehicleArea(useLocation())
  const truckType = useSelector(getTruckType, { serialNumber })
  const switchlinkedLogics = useSelector(getSwitchLinkedLogic, {
    serialNumber,
    switchId,
    truckType,
  })
  const ecpLinkedLogics = useSelector(getEcpLinkedLogics, {
    serialNumber,
    outputId,
    moduleType,
  })

  const linkedLogics =
    associatedControl === 'switch' ? switchlinkedLogics : ecpLinkedLogics

  const actionSignal =
    truckType === VEHICLE_TYPE_M2
      ? SWITCH_LINKED_REFERENECE_PARAMETERS_RECIEVED
      : SWITCH_LINKED_FEATURES_RECIEVED
  const isLoading = useMakeSelector(makeGetIsWaiting, {
    signals: [
      `${actionSignal}${serialNumber}/${switchId}`,
      `${ECP_LINKED_FEATURES_RECIEVED}${serialNumber}/${moduleType}/${outputId}`,
    ],
  })

  useEffect(() => {
    getLinkedLogicCallback()
  }, [switchId, moduleType, outputId])

  const getLinkedLogicCallback = () => {
    if (truckType === VEHICLE_TYPE_M2) {
      dispatch(
        fetchSwitchLinkedReferenceParameters({
          switchId,
          serialNumber,
        })
      )
    }

    if (truckType === VEHICLE_TYPE_CHEC_PLUS) {
      if (associatedControl === 'switch') {
        dispatch(
          fetchSwitchLinkedFeatures({
            switchId,
            serialNumber,
            associatedControl,
          })
        )
      }

      if (associatedControl === 'ecp') {
        dispatch(
          fetchEcpLinkedFeatures({
            outputId,
            moduleType,
            serialNumber,
            associatedControl,
          })
        )
      }
    }
  }

  const linkedLogicHeader =
    truckType === VEHICLE_TYPE_M2
      ? strings.reference_parameters
      : strings.features
  const List = useMemo(
    () => isArray(linkedLogics) && getList(linkedLogics),
    [linkedLogics]
  )

  const handleRefresh = () => {
    getLinkedLogicCallback()
  }

  const tooltipLoad =
    truckType !== VEHICLE_TYPE_M2
      ? strings.refresh_feature_list
      : strings.refresh_rp_list
  const tooltipLoading = strings.loading

  return (
    <div className={className}>
      {associatedControl === 'switch' && (
        <>
          <StyledParagraph size="small" weight="bold">
            {linkedLogicHeader}
            <Tooltip
              placement="right"
              title={isLoading ? tooltipLoading : tooltipLoad}
            >
              <StyledRefreshIcon
                onClick={handleRefresh}
                style={{ cursor: 'pointer' }}
              >
                {isLoading ? (
                  <svg
                    width="12"
                    height="13"
                    viewBox="0 0 12 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.239.65a6.058 6.058 0 0 1 5.428 2.126v-.851a.667.667 0 0 1 1.333 0v2.671a.667.667 0 0 1-.667.667H8.667a.667.667 0 1 1 0-1.333h1.207A4.737 4.737 0 0 0 5.41 1.973a4.73 4.73 0 0 0-2.725 1.333 4.747 4.747 0 0 0-1.358 2.718.667.667 0 1 1-1.32-.183 6.08 6.08 0 0 1 1.74-3.481A6.062 6.062 0 0 1 5.238.65zM10.673 7.176a.667.667 0 1 1 1.32.184 6.08 6.08 0 0 1-1.739 3.48 6.062 6.062 0 0 1-8.92-.416v.851a.667.667 0 1 1-1.334 0V8.604c0-.368.299-.667.667-.667h2.666a.667.667 0 1 1 0 1.333H2.127a4.725 4.725 0 0 0 7.189.624 4.747 4.747 0 0 0 1.358-2.718z"
                      fill="#07070F"
                      fillOpacity=".4"
                    />
                  </svg>
                ) : (
                  <svg
                    width="12"
                    height="13"
                    viewBox="0 0 12 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.239.65a6.058 6.058 0 0 1 5.428 2.126v-.851a.667.667 0 0 1 1.333 0v2.671a.667.667 0 0 1-.667.667H8.667a.667.667 0 1 1 0-1.333h1.207A4.737 4.737 0 0 0 5.41 1.973a4.73 4.73 0 0 0-2.725 1.333 4.747 4.747 0 0 0-1.358 2.718.667.667 0 1 1-1.32-.183 6.08 6.08 0 0 1 1.74-3.481A6.062 6.062 0 0 1 5.238.65zM10.673 7.176a.667.667 0 1 1 1.32.184 6.08 6.08 0 0 1-1.739 3.48 6.062 6.062 0 0 1-8.92-.416v.851a.667.667 0 1 1-1.334 0V8.604c0-.368.299-.667.667-.667h2.666a.667.667 0 1 1 0 1.333H2.127a4.725 4.725 0 0 0 7.189.624 4.747 4.747 0 0 0 1.358-2.718z"
                      fill="#0051C4"
                    />
                  </svg>
                )}
              </StyledRefreshIcon>
            </Tooltip>
          </StyledParagraph>
          {isNativeSwitch && (
            <Paragraph size="small" style={{ marginBottom: 8 }}>
              {strings.cannot_add_feature_to_native_switch}
            </Paragraph>
          )}
          {isLoading && <Loading size="sm" contained />}
          {!isLoading && (
            <>
              {isEmpty(linkedLogics) ? (
                <Paragraph size="small" style={{ marginBottom: '16px' }}>
                  {strings.none}
                </Paragraph>
              ) : (
                <StyledParamList
                  style={{
                    marginLeft: associatedControl === 'switch' ? -40 : 0,
                  }}
                >
                  {List}
                </StyledParamList>
              )}
            </>
          )}
        </>
      )}
      {associatedControl === 'ecp' && (
        <>
          <StyledEcpLabel>{strings.features}</StyledEcpLabel>
          {!outputId && (
            <StyledEcpBodyText>
              {strings.cannot_add_feature_to_solenoid}
            </StyledEcpBodyText>
          )}
          {outputId && isLoading && <Loading size="sm" contained />}
          {outputId && !isLoading && (
            <>
              {isEmpty(linkedLogics) ? (
                <StyledEcpBodyText>{strings.none}</StyledEcpBodyText>
              ) : (
                <StyledParamList
                  style={{
                    marginLeft: associatedControl === 'switch' ? -40 : 0,
                  }}
                >
                  {List}
                </StyledParamList>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

const getList = (list) =>
  list.map(({ uri, description, id, partNumber, revision }) => (
    <ListLogicItem key={`${uri}`}>
      <Tooltip title={description}>
        <Link to={uri}>
          {partNumber
            ? strings.feature_display_with_revision({ partNumber, revision })
            : id}
        </Link>
      </Tooltip>
    </ListLogicItem>
  ))

LinkedLogicsList.propTypes = {
  switchId: PropTypes.string,
  className: PropTypes.string,
  isNativeSwitch: PropTypes.bool,
  associatedControl: PropTypes.oneOf(['switch', 'ecp']),
  outputId: PropTypes.string,
  moduleType: PropTypes.string,
}

export default LinkedLogicsList
